import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, CheckCircle, AlertCircle, Edit, X, Search, MapPin, Building2, DollarSign, Briefcase, Eye, Star } from 'lucide-react';
import workAbroadService from '../../services/workAbroadService';
import RoleDetailsModal from './RoleDetailsModal';

const RolePreferencesSection = ({ profile, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [formData, setFormData] = useState({
    availabilityTimeline: '',
    willingToWorkShifts: false,
    willingToRelocateWithinCountry: false,
    minimumSalaryExpectation: '',
    additionalPreferences: ''
  });

  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Modal state
  const [selectedRoleForModal, setSelectedRoleForModal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [filterCountry, setFilterCountry] = useState('');

  useEffect(() => {
    fetchAvailableRoles();
  }, []);

  useEffect(() => {
    if (profile?.rolePreferences) {
      const prefs = profile.rolePreferences;
      const hasData = prefs.availabilityTimeline || (prefs.selectedRoles && prefs.selectedRoles.length > 0);
      
      setFormData({
        availabilityTimeline: prefs.availabilityTimeline || '',
        willingToWorkShifts: prefs.willingToWorkShifts || false,
        willingToRelocateWithinCountry: prefs.willingToRelocateWithinCountry || false,
        minimumSalaryExpectation: prefs.minimumSalaryExpectation || '',
        additionalPreferences: prefs.additionalPreferences || ''
      });
      
      // Set selected roles from profile
      if (prefs.selectedRoles && prefs.selectedRoles.length > 0) {
        setSelectedRoles(prefs.selectedRoles);
      }
      
      setIsEditing(!hasData);
    } else {
      setIsEditing(true);
    }
  }, [profile]);

  const fetchAvailableRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await workAbroadService.getAvailableRoles();
      setAvailableRoles(response.data || []);
    } catch (error) {
      console.error('Error fetching available roles:', error);
      setErrorMessage('Failed to load available roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRoleSelect = (role) => {
    const isAlreadySelected = selectedRoles.some(r => r.roleId === role._id);
    
    if (isAlreadySelected) {
      // Remove role and update priorities
      const newRoles = selectedRoles.filter(r => r.roleId !== role._id);
      const reorderedRoles = newRoles.map((r, index) => ({ ...r, priority: index + 1 }));
      setSelectedRoles(reorderedRoles);
    } else {
      // Add role if less than 2
      if (selectedRoles.length < 2) {
        const priority = selectedRoles.length + 1;
        setSelectedRoles([...selectedRoles, { roleId: role._id, priority, roleData: role }]);
      }
    }
    
    // Close modal after selection
    setIsModalOpen(false);
  };

  const openRoleDetails = (role) => {
    setSelectedRoleForModal(role);
    setIsModalOpen(true);
  };

  const getSelectedRoleData = (roleId) => {
    return availableRoles.find(r => r._id === roleId);
  };

  const isRoleSelected = (roleId) => {
    return selectedRoles.some(r => r.roleId === roleId);
  };

  // Filter roles based on search and filters
  const filteredRoles = availableRoles.filter(role => {
    const matchesSearch = searchTerm === '' || 
      role.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.employer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.country.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSector = filterSector === '' || role.sector === filterSector;
    const matchesCountry = filterCountry === '' || role.country === filterCountry;
    
    return matchesSearch && matchesSector && matchesCountry;
  });

  // Get unique sectors and countries for filters
  const uniqueSectors = [...new Set(availableRoles.map(r => r.sector))];
  const uniqueCountries = [...new Set(availableRoles.map(r => r.country))];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const dataToSubmit = {
        ...formData,
        selectedRoles: selectedRoles.map(r => ({ roleId: r.roleId, priority: r.priority }))
      };
      
      await workAbroadService.updateRolePreferences(dataToSubmit);
      setSuccessMessage('Role preferences saved successfully!');
      setIsEditing(false);
      if (onRefresh) onRefresh();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to save role preferences');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (profile?.rolePreferences) {
      const prefs = profile.rolePreferences;
      setFormData({
        availabilityTimeline: prefs.availabilityTimeline || '',
        willingToWorkShifts: prefs.willingToWorkShifts || false,
        willingToRelocateWithinCountry: prefs.willingToRelocateWithinCountry || false,
        minimumSalaryExpectation: prefs.minimumSalaryExpectation || '',
        additionalPreferences: prefs.additionalPreferences || ''
      });
      
      if (prefs.selectedRoles && prefs.selectedRoles.length > 0) {
        setSelectedRoles(prefs.selectedRoles);
      } else {
        setSelectedRoles([]);
      }
    }
    setIsEditing(false);
    setErrorMessage('');
  };

  const formatAvailability = (timeline) => {
    if (!timeline) return 'Not provided';
    return timeline.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header with Edit Button */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#202356] mb-2">Role Preferences</h1>
            <p className="text-gray-600">
              {isEditing ? 'Browse available roles and set your work preferences' : 'Your selected roles and preferences'}
            </p>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 border border-[#202356] text-[#202356] rounded-lg font-medium hover:bg-[#202356] hover:text-white transition-colors"
            >
              <Edit size={18} />
              Edit
            </button>
          )}
        </div>

        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
          >
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-green-700">{successMessage}</p>
          </motion.div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </motion.div>
        )}

        {/* View Mode */}
        {!isEditing ? (
          <div className="space-y-6">
            {/* Selected Roles */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">My Selected Roles</h2>
              {selectedRoles.length > 0 ? (
                <div className="space-y-4">
                  {selectedRoles.map((selected) => {
                    const role = getSelectedRoleData(selected.roleId);
                    if (!role) return null;
                    return (
                      <div key={selected.roleId} className="border border-gray-200 rounded-lg p-4 bg-gradient-to-r from-blue-50 to-white">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${selected.priority === 1 ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                                {selected.priority === 1 ? '1st Choice' : '2nd Choice'}
                              </span>
                              <h3 className="text-lg font-bold text-[#202356]">{role.title}</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <Building2 size={16} />
                                <span>{role.employer}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <MapPin size={16} />
                                <span>{role.city ? `${role.city}, ${role.country}` : role.country}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <DollarSign size={16} />
                                <span>{role.salaryRange}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500">No roles selected yet</p>
              )}
            </div>

            {/* Work Preferences */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Work Preferences</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Availability Timeline</p>
                  <p className="text-base font-medium text-gray-900">{formatAvailability(formData.availabilityTimeline)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Minimum Salary Expectation</p>
                  <p className="text-base font-medium text-gray-900">{formData.minimumSalaryExpectation || 'Not provided'}</p>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className={formData.willingToWorkShifts ? 'text-green-600' : 'text-gray-300'} />
                  <span className="text-sm text-gray-700">Willing to work shifts</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className={formData.willingToRelocateWithinCountry ? 'text-green-600' : 'text-gray-300'} />
                  <span className="text-sm text-gray-700">Willing to relocate within country</span>
                </div>
              </div>
              {formData.additionalPreferences && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-1">Additional Preferences</p>
                  <p className="text-base text-gray-900">{formData.additionalPreferences}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* My Selected Roles */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">My Selected Roles</h2>
                <span className="text-sm text-gray-600">{selectedRoles.length} / 2 selected</span>
              </div>
              {selectedRoles.length > 0 ? (
                <div className="space-y-3">
                  {selectedRoles.map((selected) => {
                    const role = getSelectedRoleData(selected.roleId);
                    if (!role) return null;
                    return (
                      <div key={selected.roleId} className="border border-gray-200 rounded-lg p-4 bg-gradient-to-r from-blue-50 to-white flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${selected.priority === 1 ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                              {selected.priority === 1 ? '1st Choice' : '2nd Choice'}
                            </span>
                            <h3 className="text-base font-bold text-[#202356]">{role.title}</h3>
                          </div>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <MapPin size={14} />
                              <span>{role.country}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Briefcase size={14} />
                              <span>{role.sector}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRoleSelect(role)}
                          className="text-red-600 hover:text-red-800 p-1"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Briefcase size={48} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600">No roles selected yet. Browse available roles below.</p>
                </div>
              )}
            </div>

            {/* Browse Available Roles */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Browse Available Roles</h2>
              
              {/* Search and Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356]"
                  />
                </div>
                <select
                  value={filterSector}
                  onChange={(e) => setFilterSector(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356]"
                >
                  <option value="">All Sectors</option>
                  {uniqueSectors.map(sector => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
                <select
                  value={filterCountry}
                  onChange={(e) => setFilterCountry(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356]"
                >
                  <option value="">All Countries</option>
                  {uniqueCountries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              {/* Roles Grid */}
              {loadingRoles ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202356] mx-auto"></div>
                  <p className="text-gray-600 mt-4">Loading available roles...</p>
                </div>
              ) : filteredRoles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto pr-2">
                  {filteredRoles.map((role) => {
                    const selected = isRoleSelected(role._id);
                    return (
                      <div
                        key={role._id}
                        className={`border rounded-lg p-4 transition-all ${
                          selected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-[#202356] hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 flex-1">{role.title}</h3>
                          {selected && <Star size={20} className="text-blue-600 fill-blue-600" />}
                        </div>
                        <div className="space-y-1 text-sm text-gray-600 mb-3">
                          <div className="flex items-center gap-1">
                            <MapPin size={14} />
                            <span>{role.city ? `${role.city}, ${role.country}` : role.country}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <DollarSign size={14} />
                            <span className="font-medium">{role.salaryRange}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => openRoleDetails(role)}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                          >
                            <Eye size={16} />
                            View Details
                          </button>
                          {selected ? (
                            <button
                              type="button"
                              onClick={() => handleRoleSelect(role)}
                              className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                            >
                              Remove
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleRoleSelect(role)}
                              disabled={selectedRoles.length >= 2}
                              className="flex-1 px-3 py-2 bg-[#202356] text-white rounded-lg text-sm font-medium hover:bg-[#2542D4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {selectedRoles.length >= 2 ? 'Max 2' : 'Select'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">No roles found matching your filters.</p>
                </div>
              )}
            </div>

            {/* Work Preferences */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Work Preferences</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Availability Timeline *
                  </label>
                  <select
                    name="availabilityTimeline"
                    value={formData.availabilityTimeline}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356]"
                  >
                    <option value="">Select timeline</option>
                    <option value="immediate">Immediate</option>
                    <option value="1_month">Within 1 Month</option>
                    <option value="2_months">Within 2 Months</option>
                    <option value="3_months">Within 3 Months</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Salary Expectation
                  </label>
                  <input
                    type="text"
                    name="minimumSalaryExpectation"
                    value={formData.minimumSalaryExpectation}
                    onChange={handleChange}
                    placeholder="e.g., £25,000 per year"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356]"
                  />
                </div>
              </div>
              <div className="space-y-3 mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="willingToWorkShifts"
                    checked={formData.willingToWorkShifts}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#202356] border-gray-300 rounded focus:ring-[#202356]"
                  />
                  <span className="text-sm text-gray-700">I am willing to work shifts (including nights and weekends)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="willingToRelocateWithinCountry"
                    checked={formData.willingToRelocateWithinCountry}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#202356] border-gray-300 rounded focus:ring-[#202356]"
                  />
                  <span className="text-sm text-gray-700">I am willing to relocate within the country</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Preferences
                </label>
                <textarea
                  name="additionalPreferences"
                  value={formData.additionalPreferences}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Any other preferences or requirements..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202356]"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X size={20} />
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>

      {/* Role Details Modal */}
      <RoleDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        role={selectedRoleForModal}
        onSelect={handleRoleSelect}
        isSelected={selectedRoleForModal ? isRoleSelected(selectedRoleForModal._id) : false}
        isMaxSelected={selectedRoles.length >= 2}
      />
    </div>
  );
};

export default RolePreferencesSection;
